import {
  PREV_BRAND_REASON_KEYS,
  type PrevBrandReasonKey,
} from '@/lib/prevBrandAnswerStorage';

/** localStorage draft for /whyldnow. */
export const WHY_LD_NOW_STORAGE_KEY = 'ldsurvey:whyLdNow';

export const WHY_LD_NOW_REASON_KEYS = PREV_BRAND_REASON_KEYS;

export type WhyLdNowReasonKey = PrevBrandReasonKey;

export type WhyLdNowReason = {
  checked: boolean;
};

export type WhyLdNowAnswers = {
  [K in WhyLdNowReasonKey]: WhyLdNowReason;
} & {
  elaboration: string;
};

const emptyReason = (): WhyLdNowReason => ({ checked: false });

export const emptyWhyLdNowAnswers = (): WhyLdNowAnswers => ({
  taste: emptyReason(),
  brandImage: emptyReason(),
  productQuality: emptyReason(),
  price: emptyReason(),
  productFeatures: emptyReason(),
  recommendedByOthers: emptyReason(),
  elaboration: '',
});

function parseReason(value: unknown): WhyLdNowReason {
  if (value === true) return { checked: true };
  if (typeof value === 'object' && value !== null && (value as { checked?: unknown }).checked === true) {
    return { checked: true };
  }
  return emptyReason();
}

export function readWhyLdNowAnswers(): WhyLdNowAnswers {
  try {
    const raw = localStorage.getItem(WHY_LD_NOW_STORAGE_KEY);
    if (!raw) return emptyWhyLdNowAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyWhyLdNowAnswers();
    const o = parsed as Record<string, unknown>;
    return {
      taste: parseReason(o.taste),
      brandImage: parseReason(o.brandImage),
      productQuality: parseReason(o.productQuality),
      price: parseReason(o.price),
      productFeatures: parseReason(o.productFeatures),
      recommendedByOthers: parseReason(o.recommendedByOthers),
      elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
    };
  } catch {
    return emptyWhyLdNowAnswers();
  }
}

export function writeWhyLdNowAnswers(data: WhyLdNowAnswers): void {
  try {
    localStorage.setItem(WHY_LD_NOW_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearWhyLdNowAnswers(): void {
  try {
    localStorage.removeItem(WHY_LD_NOW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type WhyLdNowReasonSelection = {
  type: WhyLdNowReasonKey;
  elaboration: string;
};

export function getSelectedWhyLdNowReasons(data: WhyLdNowAnswers): WhyLdNowReasonSelection[] {
  return WHY_LD_NOW_REASON_KEYS.filter((key) => data[key].checked).map((key) => ({
    type: key,
    elaboration: '',
  }));
}

export function getWhyLdNowDetail(data: WhyLdNowAnswers): string {
  return data.elaboration.trim();
}

export function isWhyLdNowComplete(): boolean {
  const data = readWhyLdNowAnswers();
  if (!WHY_LD_NOW_REASON_KEYS.some((key) => data[key].checked)) return false;
  return getWhyLdNowDetail(data).length > 0;
}
